import { createContext, useContext, useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import { useQuery } from '@apollo/react-hooks';
import type { ProjectContext } from '@atlassian/jira-providers-project-context';
import { useResource } from '@atlassian/jira-router';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context';
import { type ProjectKey, type ProjectId, toProjectId } from '@atlassian/jira-shared-types';
import {
	resourceResultToServiceQueryResult,
	type ServiceQueryResult,
} from '@atlassian/jira-software-deployments-service-query-types';
import type { IssueType, Project } from '../../common/types';
import { useGiraClient } from '../../common/utils/graphql-clients';
import type {
	jira_deploymentsPage_getProject_Query as ProjectQuery,
	jira_deploymentsPage_getProject_QueryVariables as ProjectQueryVariables,
} from './__generated_apollo__/jira_deploymentsPage_getProject_Query';
import { PROJECT_QUERY } from './gql';
import { transformQueryResult } from './utils';

export const ProjectKeyContext = createContext<ProjectKey | null>(null);

export const useProjectContextServiceQueryResult = (): ServiceQueryResult<ProjectContext> =>
	resourceResultToServiceQueryResult<ProjectContext>(useResource(projectContextResource));

export const useProjectKey = (): ProjectKey => {
	const projectKey: ProjectKey | null = useContext(ProjectKeyContext);
	if (projectKey === null) {
		throw new Error('projectKey not found in context');
	}

	return projectKey;
};

export const useProjectId = (): ProjectId | null => {
	const { data } = useProjectContextServiceQueryResult();
	return data === undefined ? null : toProjectId(`${data.projectId}`);
};

export const useProject = (): ServiceQueryResult<Project> => {
	const projectContextResult = useProjectContextServiceQueryResult();
	const projectKey: ProjectKey | null = useContext(ProjectKeyContext);
	if (projectKey === null) {
		throw new Error('projectKey not found in context');
	}
	const giraClient = useGiraClient();
	const queryResult = useQuery<ProjectQuery, ProjectQueryVariables>(PROJECT_QUERY, {
		variables: { projectKey },
		client: giraClient,
	});
	return useMemo(
		() => transformQueryResult(queryResult, projectContextResult),
		[projectContextResult, queryResult],
	);
};

type ProjectAnalyticsAttributes = {
	isSimplified: boolean;
	isProjectAdmin: boolean;
	projectId: ProjectId;
} | null;

export const useProjectAnalyticsAttributes = (eventName: string): ProjectAnalyticsAttributes => {
	const { loading, error, data } = useProjectContextServiceQueryResult();

	if (error) {
		throw new Error(
			`Loading project failed while attempting to fire ${eventName} event. Details: ${error.message}`,
		);
	}
	if (loading || data == null) {
		return null;
	}

	return {
		isSimplified: data.simplified,
		isProjectAdmin: data.isProjectAdmin ?? false,
		projectId: toProjectId(`${data.projectId}`),
	};
};

type IssueTypesLookup = {
	[key: string]: IssueType;
};
export const useIssueTypesLookup = (): IssueTypesLookup => {
	const project = useProject();
	return useMemo(() => {
		if (!project.data) {
			throw new Error('project data is undefined but is required for useIssueTypesLookup');
		}
		return keyBy(project.data.issueTypes, 'id');
	}, [project.data]);
};
